import { coerce }  from '@meltwater/coerce';
import { DocumentColumnOptions } from '../options';
import { GydaDocument } from '../../gyda-document/index.js';
import {
    CUSTOM_KEY_METRIC,
    DOCUMENT_GRID_ITEM_CLICK_EVENT_NAME,
    isValidKeyMetric
} from '../../key-metric';
import { SeriesData } from '@meltwater/mw-visualizations-core';
import { PrDocumentCard } from '../../pr-document-card';
import { version } from '../../version.js';

const [ MAJOR_VERSION ] = version.split('.');
const DOCUMENT_KEY_FOR_UNAVAILABLE_TITLE_AND_BODY = '';

/**
 * This visualizes an array of Gyda documents as a single column suitable for
 * inclusion in applications and reports in the role of a content stream.
 *
 * Series data provided to this visualization can be used to display a "key
 * metric" on the card which may not natively be a part of the document.
 *
 * @example
 * const column = document.createElement(DocumentColumn.tagName);
 * column.setData(seriesData, { companyId, documents });
 * column.setOptions({ keyMetric: 'ave', language: 'en', yAxisTitle: 'USD' });
 * container.appendChild(column);
 */
export class DocumentColumn extends HTMLElement {
    #clickListener;
    #companyId;
    #documents = [];
    #documentColumnContainer;
    #seriesData = null;
    #vizOptions = new DocumentColumnOptions({});

    /**
     * The tag name for the automatically-defined custom element.
     *
     * The value is specific to a major version so as to provide forward
     * compatibility by avoiding collisions with other copies of the library
     * that may be defined as well.
     *
     * @returns {string} The major version-specific tag name for the defined custom element
     */
    static get tagName() {
        return `pr-document-column-v${MAJOR_VERSION}`;
    }

    #buildDocumentList() {
        const documentCards = this.#documents
            .map((gydaDocument, index) => {
                if (!gydaDocument) {
                    return undefined;
                }

                const contentCard = document.createElement(PrDocumentCard.tagName);

                const key = this.#seriesData?.data[index]?.key === gydaDocument.documentId
                    ? DOCUMENT_KEY_FOR_UNAVAILABLE_TITLE_AND_BODY
                    : this.#seriesData?.data[index]?.key;

                contentCard.setData({
                    companyId: this.#companyId,
                    document: gydaDocument,
                    key,
                    keyMetric: this.#vizOptions.keyMetric,
                    keyMetricLabel: this.#vizOptions.yAxisTitle,
                    language: this.#vizOptions.language,
                    value: this.#seriesData?.data[index].value
                });

                return contentCard;
            })
            .filter(element => Boolean(element));

        this.#documentColumnContainer.replaceChildren(...documentCards);
    }

    constructor() {
        super();
        this.attachShadow({ mode: 'open' });

        this.shadowRoot.innerHTML = /*html*/`
            <style>
                :host {
                    display: block;
                    height: 100%;
                    overflow-y: scroll;
                }

                #column-container {
                    display: flex;
                    flex-direction: column;
                    gap: 2px;
                }

                ${PrDocumentCard.tagName} {
                    border: 1px solid var(--flux-border-color);
                    box-shadow: var(--flux-shadow-1);
                    flex: 1 1 auto;
                    height: 278px;
                }
            </style>
            <div id="column-container"></div>
        `;
        this.#documentColumnContainer = this.shadowRoot.querySelector('#column-container');
    }

    /**
     * Render the column according to the data and options when attaching to
     * the DOM.
     *
     * @returns {undefined}
     */
    connectedCallback() {
        this.render();
    }

    /**
     * Set the data to display in the document column
     *
     * @example
     * const column = document.createElement(DocumentColumn.tagName);
     * column.setData(seriesData, { companyId, documents });
     *
     * @param {SeriesData} [seriesData=null] - The series data for any key metric values to display. The order **must** align with the documents. If provided, the key value will be shown as the document title.
     * @param {object} metadata - The metadata
     * @param {string} metadata.companyId - The companyId to use for access permission with the premium document viewer
     * @param {GydaDocument[]} metadata.documents - The documents to render in the column
     *
     * @returns {undefined}
     */
    setData(seriesData = null, { companyId, documents }) {
        this.#companyId = companyId;
        this.#documents = documents.map(document => (document !== null ? coerce(document, GydaDocument, 'document must be a GydaDocument') : null));
        this.#seriesData = seriesData !== null
            ? coerce(seriesData, SeriesData, 'seriesData must be coerce-able to SeriesData')
            : null;
    }

    /**
     * Apply optional customizations to the display of the document column.
     *
     * @example
     * const column = document.createElement(DocumentColumn.tagName);
     * column.setOptions({ keyMetric: 'ave', language: 'en', yAxisTitle: 'USD' });
     *
     * @param {DocumentColumnOptions} options - The options to apply to the document column
     *
     * @returns {undefined}
     */
    setOptions(options) {
        const vizOptions = coerce(options, DocumentColumnOptions, 'options must be valid DocumentColumnOptions');

        if (!isValidKeyMetric(vizOptions.keyMetric)) {
            throw new RangeError(`A valid key metric value or constant must be provided. Provided value: ${vizOptions.keyMetric?.toString()}`);
        }

        if (vizOptions.keyMetric === CUSTOM_KEY_METRIC && (this.#seriesData === null || vizOptions.yAxisTitle === undefined)) {
            throw new RangeError(`A custom key metric value requires seriesData for the metric values and a yAxisTitle for the metric label. Provided seriesData length: ${this.#seriesData?.data.length}; Provided yAxisTitle: ${vizOptions.yAxisTitle}`);
        }

        this.#vizOptions = vizOptions;

        if (this.#clickListener) {
            this.#documentColumnContainer.removeEventListener(DOCUMENT_GRID_ITEM_CLICK_EVENT_NAME, this.#clickListener);
            this.#clickListener = null;
        }
    }

    /**
     * Render the column according to its data and options.
     *
     * This is only necessary to handle changes *after* the element has been
     * attached to the DOM.
     *
     * @returns {undefined}
     */
    render() {
        this.#buildDocumentList();
    }
}

if (!customElements.get(DocumentColumn.tagName)) {
    customElements.define(DocumentColumn.tagName, DocumentColumn);
}
